/* eslint-disable max-lines */
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PersonAffiliationDialog,
  type PersonAffiliationDialogState,
} from '@/components/contacts/affiliations/PersonAffiliationDialog'
import { DetailPage } from '@/components/templates/detail-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { ImageUpload } from '@/components/ui/image-upload'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProfilePhotoDisclosure } from '@/components/contacts/ProfilePhotoDisclosure'
import { ApiError, deleteJSON, getJSON, postFormData } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'
import type { Person, ProfileAffiliation } from '@/lib/contacts/types'
import {
  ContactFinancialTransactionsSection,
  emptyFinancialTransactions,
} from '@/pages/ContactFinancialTransactionsSection'
import { ContactMarketingOptOuts } from '@/pages/ContactMarketingOptOuts'
import { ContactPostalAddressFields } from '@/pages/ContactPostalAddressFields'
import type { Invoice } from '@/pages/InvoicesPage.types'
import { ContactEmailHistory } from '@/pages/EmailLogPage'
import { ContactGroupsTab } from '@/pages/ContactGroupsTab'
import { ContactGivingTab } from '@/pages/ContactGivingTab'
import { PersonMembershipTab } from '@/pages/PersonMembershipTab'
import { ProfileActions } from '@/pages/ProfileActions'
import { ProfileField } from '@/pages/ProfileField'
import { EngagementPanel, type EngagementDetail } from '@/pages/EngagementPanel'
import { EngagementProfileField } from '@/pages/EngagementProfileField'
import { VisibilityDiagnosticSection } from '@/pages/VisibilityDiagnosticSection'
import { PersonGivingTab } from '@/pages/PersonGivingTab'
import { canView, isViewOnlyRole, type StaffCapabilities } from '@/pages/staffCapabilities'
import { ContactTimelineTab } from '@/pages/ContactTimelineTab'
import { useContactProfileTab } from './useContactProfileTab'
import { ProfilePinnedBanner } from '@/pages/ProfilePinnedBanner'
import { ContactLearningPanel } from '@/components/courses/ContactLearningPanel'

interface PersonResponse {
  person: Person
}

export interface PersonProfilePageProps {
  contactId: number
  onBack: () => void
  backLabel?: string
  currentAssociationDate?: string
  currentAssociationTimeZone?: string
  onEdit: () => void
  onLifecycleComplete: () => void
  onOpenInvoice?: (invoice: Invoice) => void
  capabilities?: StaffCapabilities
}

function usePersonEngagementQuery(contactId: number) {
  return useQuery({
    queryKey: ['person-engagement', contactId],
    queryFn: () =>
      getJSON<{ engagement_score: EngagementDetail }>(
        `/api/v1/persons/${contactId}/engagement_score`,
      ),
    retry: false,
  })
}

// eslint-disable-next-line max-lines-per-function, complexity
export function PersonProfilePage({
  contactId,
  onBack,
  backLabel = 'Contacts',
  currentAssociationDate,
  currentAssociationTimeZone,
  onEdit,
  onLifecycleComplete,
  onOpenInvoice,
  capabilities,
}: PersonProfilePageProps) {
  const [dialog, setDialog] = React.useState<PersonAffiliationDialogState | null>(null)
  const tabs = [
    ['profile', 'Profile'],
    ...(capabilityVisible(capabilities, 'membership') ? [['membership', 'Membership']] : []),
    ['affiliations', 'Affiliations'],
    ['groups', 'Groups'],
    ...(capabilityVisible(capabilities, 'finance')
      ? [
          ['transactions', 'Transactions'],
          ['giving', 'Giving'],
        ]
      : []),
    ...(capabilityVisible(capabilities, 'communications') ? [['emails', 'Emails']] : []),
    ['timeline', 'Timeline'],
    ...(capabilityVisible(capabilities, 'membership') ? [['engagement', 'Engagement']] : []),
  ]
  const [activeTab, setActiveTab] = useContactProfileTab(tabs.map(([value]) => value))
  const personQuery = useQuery({
    queryKey: ['person', contactId],
    queryFn: () => getJSON<PersonResponse>(`/api/v1/persons/${contactId}`),
    retry: false,
  })
  const engagementQuery = usePersonEngagementQuery(contactId)

  if (personQuery.isLoading) {
    return <EmptyState title="Loading contact" />
  }

  if (personQuery.isError || !personQuery.data) {
    return <EmptyState title="Contact unavailable" />
  }

  const person = personQuery.data.person

  return (
    <DetailPage
      title={person.name}
      subtitle={person.email ?? undefined}
      badge={<Badge variant="person">{person.contact_type}</Badge>}
      actions={
        <ProfileActions
          actions={person.profile_actions}
          contactId={person.id}
          contactName={person.name}
          membership={person.membership}
          onEdit={onEdit}
          onLifecycleComplete={onLifecycleComplete}
        />
      }
      backHref="#"
      backLabel={backLabel}
      onBack={onBack}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {tabs.map(([value, label]) => (
            <TabsTrigger key={value} value={value}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profile">
          <div className="space-y-5">
            <ProfilePinnedBanner
              note={person.pinned_note}
              onOpenTimeline={() => setActiveTab('timeline')}
            />
            <PersonProfileFields
              person={person}
              timeZone={currentAssociationTimeZone}
              onOptOutCleared={() => personQuery.refetch()}
            />
            <VisibilityDiagnosticSection person={person} />
            <ContactLearningPanel person={person} capabilities={capabilities} />
          </div>
        </TabsContent>

        {capabilityVisible(capabilities, 'membership') && (
          <TabsContent value="membership">
            <PersonMembershipTab
              person={person}
              contactId={contactId}
              currentAssociationDate={currentAssociationDate}
              currentAssociationTimeZone={currentAssociationTimeZone}
            />
          </TabsContent>
        )}

        <TabsContent value="affiliations">
          <PersonAffiliationsTab
            person={person}
            onAdd={() => setDialog({ kind: 'add' })}
            onEdit={(affiliation) => setDialog({ kind: 'edit', affiliation })}
          />
        </TabsContent>

        <TabsContent value="groups">
          <ContactGroupsTab
            contactId={contactId}
            groups={person.groups ?? []}
            profileQueryKey={['person', contactId]}
          />
        </TabsContent>

        {capabilityVisible(capabilities, 'finance') && (
          <TabsContent value="transactions">
            <ContactFinancialTransactionsSection
              contact={person}
              transactions={person.financial_transactions ?? emptyFinancialTransactions()}
              onOpenInvoice={onOpenInvoice}
              onRefundRecorded={() => personQuery.refetch()}
            />
          </TabsContent>
        )}

        {capabilityVisible(capabilities, 'finance') && (
          <TabsContent value="giving">
            <ContactGivingTab giving={person.giving} />
            <PersonGivingTab contactId={contactId} />
          </TabsContent>
        )}

        {capabilityVisible(capabilities, 'communications') && (
          <TabsContent value="emails">
            <ContactEmailHistory contactId={contactId} />
          </TabsContent>
        )}

        <TabsContent value="timeline">
          <ContactTimelineTab
            contactId={contactId}
            associationDate={currentAssociationDate}
            tombstone={person.merged_into ?? undefined}
            canWrite={!capabilities || !isViewOnlyRole(capabilities)}
            isAdmin={Boolean(capabilities?.contacts?.manage)}
            onContactChanged={() => void personQuery.refetch()}
          />
        </TabsContent>

        {capabilityVisible(capabilities, 'membership') && (
          <TabsContent value="engagement">
            <EngagementContent query={engagementQuery} />
          </TabsContent>
        )}
      </Tabs>

      {dialog && (
        <PersonAffiliationDialog person={person} dialog={dialog} onClose={() => setDialog(null)} />
      )}
    </DetailPage>
  )
}

function capabilityVisible(
  capabilities: StaffCapabilities | undefined,
  module: keyof StaffCapabilities,
) {
  return !capabilities || canView(capabilities, module as Parameters<typeof canView>[1])
}

function EngagementContent({ query }: { query: ReturnType<typeof usePersonEngagementQuery> }) {
  if (query.isLoading) return <EmptyState title="Loading Engagement" />
  if (query.isError || !query.data) return <EmptyState title="Engagement unavailable" />
  return <EngagementPanel detail={query.data.engagement_score} />
}

export default PersonProfilePage

function PersonProfileFields({
  person,
  timeZone,
  onOptOutCleared,
}: {
  person: Person
  timeZone?: string
  onOptOutCleared: () => void
}) {
  const fields = person.custom_field_values ?? []

  return (
    <>
      <ProfilePhotoControl person={person} />
      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
        <ProfileField label="Full name" value={person.name} />
        <ProfileField label="Email" value={person.email ?? '-'} />
        <ProfileField label="Phone" value={person.phone ?? '-'} />
        <ProfileField label="VAT Number" value={person.vat_number ?? '-'} />
        <ContactPostalAddressFields contact={person} />
        <ProfileField
          label="Last portal sign-in"
          value={
            person.last_portal_sign_in_at
              ? displayDateTime(person.last_portal_sign_in_at, timeZone)
              : 'Never'
          }
        />
        <ProfileField label="Tax status" value={person.tax_exempt ? 'Tax-Exempt' : 'Taxable'} />
        {person.engagement_score && <EngagementProfileField score={person.engagement_score} />}
        <ContactMarketingOptOuts
          contactId={person.id}
          marketingOptOuts={person.marketing_opt_outs ?? []}
          onCleared={onOptOutCleared}
        />
        {person.tax_exempt && (
          <>
            <ProfileField label="Exempt reason" value={person.tax_exempt_reason ?? '-'} />
            <ProfileField label="Certificate note" value={person.tax_exempt_note ?? '-'} />
          </>
        )}
        {fields.map((field) => (
          <ProfileField key={field.label} label={field.label} value={field.display_value} />
        ))}
      </dl>
      {fields.length === 0 && (
        <p className="pt-5 text-body-sm text-muted-foreground">No custom field values recorded.</p>
      )}
    </>
  )
}

export function ProfilePhotoControl({ person }: { person: Person }) {
  const queryClient = useQueryClient()
  const [removeOpen, setRemoveOpen] = React.useState(false)
  const upload = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData()
      body.append('file', file)
      return postFormData(`/api/v1/contacts/${person.id}/profile-photo`, body)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['person', person.id] }),
  })
  const remove = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/contacts/${person.id}/profile-photo`),
    onSuccess: () => {
      setRemoveOpen(false)
      queryClient.invalidateQueries({ queryKey: ['person', person.id] })
    },
  })
  const photo = person.profile_photo
  const error = upload.error ?? remove.error
  const errorMessage = profilePhotoErrorMessage(error)

  return (
    <ProfilePhotoDisclosure name={person.name} url={photo?.thumbnail_url}>
      <section className="space-y-2" aria-labelledby="profile-photo-heading">
        <div>
          <h3 id="profile-photo-heading" className="text-body font-semibold text-foreground">
            Profile Photo
          </h3>
          <p className="text-body-sm text-muted-foreground">JPEG, PNG, or WebP, up to 5 MB.</p>
        </div>
        <ImageUpload
          image={photo ? { filename: photo.filename, url: photo.thumbnail_url } : null}
          label="Profile Photo"
          pending={upload.isPending || remove.isPending}
          onChoose={(file) => upload.mutate(file)}
          onRemove={() => setRemoveOpen(true)}
        />
        {errorMessage && <Alert>{errorMessage}</Alert>}
        <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Profile Photo?</DialogTitle>
              <DialogDescription>This deletes the image and cannot be undone.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="button" variant="destructive" onClick={() => remove.mutate()}>
                Remove photo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </ProfilePhotoDisclosure>
  )
}

function profilePhotoErrorMessage(error: Error | null): string | null {
  if (!error) return null
  if (error instanceof ApiError && typeof error.body === 'object' && error.body !== null) {
    const errors = (error.body as { errors?: { profile_photo?: string[] } }).errors
    const message = errors?.profile_photo?.[0]
    if (message) return message
  }
  return error.message
}

function PersonAffiliationsTab({
  person,
  onAdd,
  onEdit,
}: {
  person: Person
  onAdd: () => void
  onEdit: (affiliation: ProfileAffiliation) => void
}) {
  return (
    <>
      <div className="pb-3">
        <Button
          type="button"
          variant="outline"
          className="h-auto border-dashed px-1.5 py-0.5 text-micro"
          onClick={onAdd}
        >
          + Add affiliation
        </Button>
      </div>
      {person.affiliations.length === 0 ? (
        <EmptyState title="No Affiliations recorded" className="items-start px-0 text-left" />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {person.affiliations.map((affiliation) => (
                <TableRow key={affiliation.id}>
                  <TableCell>{affiliation.organization}</TableCell>
                  <TableCell className="text-muted-foreground">{affiliation.role}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(affiliation)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
